import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isEqual } from 'lodash';
import { useHistory, useLocation, useParams } from 'react-router';
import { useDataset } from '../../api/hooks/useDataset';
import { useForm } from '../../api/hooks/useForm';
import { useInsertDatasetData } from '../../api/hooks/useInsertDatasetData';
import { useUpdateDatasetData } from '../../api/hooks/useUpdateDatasetData';
import { createFormItemsMap } from '../../utils/formItems/createFormItemMap';
import { normalizeFormItemMap } from '../../utils/formItems/normalizeFormItemMap';
import {
  ControlSelectionMode,
  Dataset,
  FilterError,
  Form,
  JSONObject,
  GlobalLocationStateType,
  MultiSelectMode,
  StaticControlItem,
  GridInputValue,
} from '../../types/types';
import { validateSubmitData } from './utils/validateSubmitData';
import { findFirstEmptyMandatoryField } from './utils/findFirstEmptyMandatoryField';
import { getMountedFormFieldNames } from '../../utils/forms/getMountedFormFieldNames';
import { useTranslate } from '../../hooks/context/useTranslate';
import { createRequestBody } from './utils/createRequestBody';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { DatasetFormDataContextType, FormTokenNode } from '../../types/context';
import { getFormTypeFromPathname } from '../../utils/forms/getFormTypeFromPathname';
import { useFormTokenChainContext } from '../../hooks/context/useFormTokenChainContext';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import { useStoreAlwaysAction } from '../../hooks/responseActions/forms/useStoreAlwaysAction';
import { useSetFocusAction } from '../../hooks/responseActions/forms/useSetFocusAction';
import useRoutes from '../../hooks/useRoutes';
import { getActionFromFormRouteUrl } from '../../utils/forms/getActionFromFormRouteUrl';
import { focusAswerErrorField } from '../../utils/forms/focusAswerErrorField';
import { useDebouncedEffect } from '../../hooks/useDebouncedEffect';
import { processFetchedSlaveDataset } from '../../utils/processFetchedSlaveDataset';
import { getFormLabel } from '../../utils/forms/getFormLabel';
import { useActualFormToken } from './hooks/useActualFormToken';
import { useResolvedFormDataset } from './hooks/useResolvedFormDataset';
import { useSourceDatasetConditions } from './hooks/useSourceDatasetConditions';
import { useFormItemDecorators } from './hooks/useFormItemDecorators';
import { fetchInsertRowData } from './utils/fetchInsertRowData';
import { fetchEditRowData } from './utils/fetchEditRowData';
import { submitInsertForm } from './utils/submitInsertForm';
import { submitEditForm } from './utils/submitEditForm';
import { shouldRefetchAfterBulkFormExit } from './utils/shouldRefetchAfterBulkFormExit';
import { shouldReturnToListAfterBulkSubmit } from './utils/shouldReturnToListAfterBulkSubmit';
import { useSelectedRowsContext } from '../../hooks/context/useSelectedRowsContext';
import { useSlaveDatasetContext } from '../../hooks/context/useSlaveDatasetContext';
import { createRelevantFormData } from './utils/createRelevantFormData';
import { useDatasetData } from '../../api/hooks/useDatasetData';
import { useDatasetDataRes } from '../../api/types/types';
import { getDataKey } from '../../utils/getDataKey';
import { useUserState } from '../../hooks/context/useUserState';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import resolveFromUrl from './utils/resolveFromUrl';
import RequestDataProvider from '../RequestDataContext';
import FormHandlersProvider from '../FormHandlersContext/FormHandlersContext';
import { FormApiMethodsRequestData } from '../../api/types/types';
import { normalizeFormDataToCompare } from './utils/normalizeFormDataToCompare';
import { getFormApiMethodsRequestData } from '../../api/utils/requestData/getFormApiMethodsRequestData';
import { isDatasetDefinitionReloadRequired } from './utils/isDatasetDefinitionReloadRequired';

const defaultValues: DatasetFormDataContextType = {
  isProvided: false,
  actualToken: null,
  apiError: undefined,
  dataset: undefined,
  isLoading: true,
  isSubmitting: false,
  form: undefined,
  formData: null,
  formType: 'insert',
  formItems: [],
  formErrors: {},
  initialValues: null,
  setFormErrors: () => {
    throw new Error('setFormErrors must be implemented');
  },
  currentSelectedRowIndex: 0,
  multiSelectMode: 'sequential',
  controlSelectionMode: undefined,
  formValuesChanged: false,
  forceSave: false,
  setForceSave: () => {
    throw new Error('setForceSave must be implemented');
  },
  submitDatasetForm: () => {
    throw new Error('submitDatasetForm must be implemented');
  },
  closeDatasetForm: () => {
    throw new Error('closeDatasetForm must be implemented');
  },
  showNextRow: () => {
    throw new Error('showNextRow must be implemented');
  },
  showPreviousRow: () => {
    throw new Error('showPreviousRow must be implemented');
  },
  submitAndNext: () => {
    throw new Error('submitAndNext must be implemented');
  },
  handleInputChange: () => {
    throw new Error('handleInputChange must be implemented');
  },
  patchFormData: () => {
    throw new Error('patchFormData must be implemented');
  },
  // No-op outside provider — only called from useFormOptionsPrefetch inside the tree
  prefetchedFormOptions: null,
  setPrefetchedFormOptions: () => {
    throw new Error('setPrefetchedFormOptions must be implemented');
  },
  standaloneFormSubmitCompleted: false,
};

export const DatasetFormDataContext =
  createContext<DatasetFormDataContextType>(defaultValues);

const DatasetFormDataContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(
    defaultValues.formData
  );
  const [formErrors, setFormErrors] = useState<Record<string, FilterError>>(
    defaultValues.formErrors
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Standalone menu form submitted — Form page swaps to the completion screen
  const [standaloneFormSubmitCompleted, setStandaloneFormSubmitCompleted] =
    useState<boolean>(false);
  // null = prefetch in progress; Record = prefetch done (populated by useFormOptionsPrefetch)
  const [prefetchedFormOptions, setPrefetchedFormOptions] = useState<Record<
    string,
    StaticControlItem[]
  > | null>(null);
  const [forceSave, setForceSave] = useState<boolean>(false);

  // Register form response actions
  useStoreAlwaysAction(setForceSave);
  useSetFocusAction();

  const { t } = useTranslate();
  const history = useHistory();
  const routes = useRoutes();
  const { formId, itemId, datasetId, subsetId, subsetItemId, sourceItemId } =
    useParams<{
      formId: string;
      datasetId: string;
      itemId?: string;
      subsetId?: string;
      subsetItemId?: string;
      sourceItemId?: string;
    }>();
  const { pathname, search, state } = useLocation<GlobalLocationStateType>();
  const {
    fetchDatasetData,
    setSlaveDatasets,
    setSlaveLazyStates,
    dataset: masterDataset,
    data: masterDatasetdData,
    dataHookLoadingDatasetId,
  } = useDatasetContext();
  const { selectedRows, setSelectedRows, resetSelectedRows, setExpandedRow } =
    useSelectedRowsContext();
  const { invalidateFetchedCombo: invalidateSlaveFetchedCombo } =
    useSlaveDatasetContext();
  const {
    getActualTokenFromUrl,
    updateTokenValues,
    removeDescendantTokens,
    createRootToken,
    popToken,
    formTokens,
  } = useFormTokenChainContext();
  const {
    state: { token: sessionToken },
  } = useUserState();
  const { getResponseActionHandler } = useResponseActionsContext();
  const { displaySlaveDataset } = useSourceDatasetConditions();
  const dataset = useResolvedFormDataset();
  const actualToken = useActualFormToken();
  // Lazy init from token meta so reloads keep the user on the row they were editing.
  // Must run before the debounced fetchFormData effect captures the closure (its deps
  // are [dataset] only, so a post-mount useEffect update would be read too late).
  const [currentSelectedRowIndex, setCurrentSelectedRowIndex] =
    useState<number>(() => {
      const savedIndex = actualToken?.meta?.currentSelectedRowIndex as
        | number
        | undefined;
      return typeof savedIndex === 'number'
        ? savedIndex
        : defaultValues.currentSelectedRowIndex;
    });
  const { insert } = useInsertDatasetData();
  const { update } = useUpdateDatasetData();
  const formType = useMemo(() => getFormTypeFromPathname(pathname), [pathname]);
  const multiSelectMode = useMemo(
    () =>
      (actualToken?.meta?.requestData?.['mode'] as MultiSelectMode) ||
      'sequential',
    [actualToken]
  );

  const { formItems, initializeFormItems } = useFormItemDecorators({
    formData,
    actualToken,
    pathname,
    multiSelectMode,
  });

  // selectionMode of the control that opened this form; suppresses paginator and save-and-next for 'dontcare'
  const controlSelectionMode = useMemo(
    () =>
      actualToken?.meta?.controlSelectionMode as
        | ControlSelectionMode
        | undefined,
    [actualToken]
  );

  const handleApiError = useApiErrorHandling();
  const {
    data: rowData,
    fetch: fetchData,
    detailedFetch: detailedFetchData,
    error: dataError,
    isLoading: dataLoading,
  } = useDatasetData();

  const { fetch: fetchDataset } = useDataset(subsetId ?? '');
  const {
    data: form,
    fetch: fetchForm,
    error: formError,
    isLoading: formLoading,
  } = useForm(formId);

  const [isFetchingIncomingRow, setIsFetchingIncomingRow] = useState(false);
  // Synchronous lock — drops paginator clicks that arrive while a row fetch is
  // already in flight. Ref (not state) to avoid stale closures and because the
  // UI intentionally stays unchanged (no disabled/loading indicator on arrows).
  const isFetchingIncomingRowRef = useRef<boolean>(false);
  const apiError =
    formError || (isFetchingIncomingRow ? undefined : dataError?.error);
  const canAssignBreadCrumb =
    actualToken && formType && form && dataset && pathname;

  // Dirty = current formData differs from the pristine (server-derived) snapshot.
  // Uses createRelevantFormData so comparison matches what setFormInputData applied.
  const formValuesChanged = useMemo(() => {
    if (!rowData || !form || !formData) return false;
    // Seed expected keys so multiselects (and other emptiable fields) missing
    // from rowData compare equal to the same key cleared back to '' / undefined.
    const expectedKeys = (form.items ?? [])
      .map((i) => i.name)
      .filter((n): n is string => Boolean(n));
    const pristine = createRelevantFormData(rowData, form);
    const pristineNormalized = normalizeFormDataToCompare(
      pristine,
      expectedKeys
    );
    const formDataNormalized = normalizeFormDataToCompare(
      formData,
      expectedKeys
    );
    return !isEqual(formDataNormalized, pristineNormalized);
  }, [formData, rowData, form]);

  const dataFromTokenInitialised = useRef<boolean>(false);
  const descendantRemoveChecked = useRef<boolean>(false);
  // Session-scoped "server state was mutated" flag.
  // Ref (not state) — purely controls the close-path refetch decision, never rendering.
  const hasSubmittedSuccessfully = useRef<boolean>(false);

  const isSubsetForm = subsetId && itemId;

  const assignValuesFromToken = (relevantFormData: Record<string, unknown>) => {
    const newRelevantFormData = { ...relevantFormData };
    const tokenFromUrl = getActualTokenFromUrl();

    if (tokenFromUrl) {
      const tokenValues = tokenFromUrl.values;

      Object.keys(tokenValues).forEach((tokenValueKey) => {
        newRelevantFormData[tokenValueKey] = tokenValues[tokenValueKey];
      });
    }

    return newRelevantFormData;
  };

  const setFormInputData = (
    rowData: useDatasetDataRes,
    form: Form,
    assignFromToken: boolean
  ) => {
    if (rowData && form) {
      const relevantFormData = createRelevantFormData(rowData, form);

      if (assignFromToken) {
        const formDataWithTokenValues = assignValuesFromToken(relevantFormData);
        setFormData(formDataWithTokenValues);
        return;
      }

      setFormData(relevantFormData);
    }
  };

  const closeDatasetForm = useCallback(
    async (
      customRedirectUrl?: string,
      additionalState?: Record<string, string>
    ) => {
      // Clear selection AND selectAll checkbox + virtual row count — prevents the
      // "select all" header checkbox from staying checked after the form closes
      // while selectedRows is already empty. Also prevents filterFirst reopening.
      resetSelectedRows();
      const token = getActualTokenFromUrl();
      // Root token = we are about to return to the dataset list, not to a parent form
      const isExitingForms = token?.parentTokenId === null;
      const submitted = hasSubmittedSuccessfully.current;

      // Define flags propagated through  router state that modify application behaviour
      const routerState: GlobalLocationStateType = isExitingForms
        ? {
            // consumed by master grid
            fromForms: true,
            submitted,
            isDatasetDefinitionReloadRequired:
              isDatasetDefinitionReloadRequired(
                token.meta?.requestData?.['formId'],
                submitted
              ),
            // consumed by slave grid
            subsetItemId: isSubsetForm ? subsetItemId : undefined,
            subsetSubmitted: isSubsetForm ? submitted : undefined,
            subsetFromForms: isSubsetForm ? true : undefined,
            // Only for a filterFirst dataset the form belongs to — a standalone
            // (menu) form returns via plain navigation, so the stale context
            // dataset must not skip the filter page there
            omitFilterFirstFilter:
              datasetId && masterDataset?.filterFirst ? true : undefined,
            // Toggled only when the base dataset is filterFirst and has no data
            // yet — the return guard then force-fetches it (the filter modal is
            // skipped, so nothing else would). Skip it when a long-running poll
            // for that dataset is still in flight: it survives the form and will
            // deliver the data itself, so forcing a fetch would just discard it.
            fetchDataOnFilterFirstDataset:
              datasetId &&
              masterDataset?.filterFirst &&
              !masterDatasetdData &&
              dataHookLoadingDatasetId !== masterDataset?.datasetId
                ? true
                : undefined,
            // Entering the form from an external url: the locate probe loads
            // the row within the full dataset, replacing the single-record
            // external data. filterFirst defaults are not seeded under the
            // external display, so the probe runs unfiltered there.
            ...(token.meta?.enteredFromExternalUrl
              ? {
                  forceFindInDataset: true,
                  forceNewData:
                    !masterDatasetdData || masterDatasetdData?.length <= 1
                      ? true
                      : undefined,
                }
              : {}),
            ...additionalState,
          }
        : // Nested (returning to a parent form/reference): forward only the
          // explicit extras (e.g. referenceInsertedId for FullscreenReference).
          { ...additionalState };

      // Refresh list cache only if a server write actually occurred during this session.
      // Covers the "submitAndNext then Cancel" case where submit alone does not refetch.
      if (isExitingForms && submitted) {
        hasSubmittedSuccessfully.current = false;
      }

      if (
        shouldRefetchAfterBulkFormExit({
          multiSelectMode,
          isExitingForms,
          submitted,
          isSubsetFormToken: Boolean(token?.meta?.requestData?.['master']),
          returnUrl: customRedirectUrl ?? token?.fromUrl ?? '',
        })
      ) {
        fetchDatasetData();
      }

      // The submit may have changed subset rows without touching the master
      // row, and the slave combo cache survives the form page — drop it so
      // the post-return locate refetches the open subset. Subset-opened
      // forms relocate via the subsetFromForms flags instead.
      if (isExitingForms && submitted && !isSubsetForm) {
        invalidateSlaveFetchedCombo();
      }

      // Reset expanded row, will searched again after redirect
      setExpandedRow(null);

      if (token) {
        popToken(token.id);
        if (token.fromUrl) {
          // Standalone menu form cancel → dashboard, not the unrelated origin dataset
          const standaloneRedirect =
            isExitingForms && !datasetId ? routes.dashboards() : undefined;
          // Keep /detail/ — useFormReturnDetailFetchGuard probes the row.
          history.replace(
            customRedirectUrl ?? standaloneRedirect ?? token.fromUrl,
            routerState
          );
          return;
        }
      }

      let fallbackUrl: string = datasetId
        ? routes.datasetList(datasetId)
        : routes.dashboards();

      if (subsetId && itemId) {
        fallbackUrl = routes.subsetDetail(datasetId, itemId);
      }

      if (customRedirectUrl) {
        fallbackUrl = customRedirectUrl;
      }

      //Fallback
      history.replace(fallbackUrl, routerState);
    },
    [
      getActualTokenFromUrl,
      popToken,
      history,
      datasetId,
      formTokens.nodes,
      dataset,
      fetchDatasetData,
      multiSelectMode,
      resetSelectedRows,
      invalidateSlaveFetchedCombo,
      masterDataset,
      masterDatasetdData,
      dataHookLoadingDatasetId,
    ]
  );

  const submitDatasetForm = useCallback(
    // afterSuccess: when provided, called instead of closeDatasetForm (used by submitAndNext)
    async (afterSuccess?: () => void) => {
      // Drop repeat clicks while a submit request is already in flight
      if (isSubmitting) return;

      const token = getActualTokenFromUrl();
      const mountedFields = getMountedFormFieldNames();
      const isMounted = (name: string): boolean =>
        mountedFields.has(name.toLowerCase());
      const hasValidData = validateSubmitData(
        t,
        formData,
        formItems,
        formErrors,
        setFormErrors,
        isMounted
      );

      if (!hasValidData) {
        const fieldName = findFirstEmptyMandatoryField(
          formData,
          formItems,
          isMounted
        );
        if (fieldName) getResponseActionHandler?.('setFocus')?.(fieldName);
        return;
      }

      if (form) {
        const requestBody = createRequestBody(
          form,
          formData,
          rowData?.data?.[0],
          // insert/copy POST a new record, edit PUTs the existing one — the
          // definition saved.onPOST/onPUT rules follow the method.
          formType === 'edit' ? 'PUT' : 'POST'
        );
        let response:
          | { response: any | null; error: Error | unknown }
          | undefined = undefined;

        setIsSubmitting(true);
        try {
          // copy submits as a new record — same API path (create-item) as insert
          if (formType === 'insert' || formType === 'copy') {
            response = await submitInsertForm(
              form,
              requestBody,
              actualToken,
              sessionToken,
              insert,
              handleApiError,
              getResponseActionHandler
            );
          }

          if (formType === 'edit') {
            const rowId =
              selectedRows[currentSelectedRowIndex][dataset?.idItem];
            response = await submitEditForm(
              form,
              requestBody,
              rowId,
              actualToken,
              sessionToken,
              update,
              handleApiError,
              getResponseActionHandler
            );
          }
        } finally {
          setIsSubmitting(false);
        }

        // On ASWER submit error, focus the field named in error.focusField.
        if (response?.error) {
          focusAswerErrorField(response.error, getResponseActionHandler);
        }

        if (response && response?.error === null) {
          // Mark the session dirty so closeDatasetForm refetches the list on exit
          // (including the submitAndNext → ... → Cancel path).
          hasSubmittedSuccessfully.current = true;

          // Redirect url on new item when submitted insert or copy form
          let newItemUrl: string | undefined = undefined;
          let additionalRouterState: Record<string, string> | undefined =
            undefined;

          // Root token only; standalone (menu) forms have no datasetId, so skip the new-item redirect and fall back to token.fromUrl.
          if (
            (formType === 'insert' || formType === 'copy') &&
            token?.parentTokenId === null &&
            datasetId
          ) {
            const newId = response.response?.id;

            if (newId) {
              newItemUrl = isSubsetForm
                ? routes.subset(datasetId, itemId, subsetId)
                : routes.subsetDetail(datasetId, String(newId));

              additionalRouterState = isSubsetForm
                ? { subsetItemId: String(newId) }
                : undefined;
            }
          }

          // Insert/copy opened from a FullscreenReference (nested token whose
          // fromUrl is the reference URL): pass the new id via router state so
          // the reference auto-selects it on return. No token mutation.
          if (
            (formType === 'insert' || formType === 'copy') &&
            token?.parentTokenId &&
            token.fromUrl?.includes('/reference/')
          ) {
            const newId = response.response?.id;

            if (newId) {
              additionalRouterState = { referenceInsertedId: String(newId) };
            }
          }

          // Bulk edit opened from the fullscreen detail returns to the list, not to the single row.
          let bulkDetailRedirect: string | undefined = undefined;

          if (
            shouldReturnToListAfterBulkSubmit({
              datasetId,
              multiSelectMode,
              isRootToken: token?.parentTokenId === null,
              isSubsetForm: Boolean(isSubsetForm),
              fromUrl: token?.fromUrl,
            })
          ) {
            bulkDetailRedirect = routes.datasetList(datasetId);
          }

          if (afterSuccess) {
            afterSuccess();
            return;
          }
          // Standalone menu form: show the completion screen instead of
          // jumping to the unrelated origin dataset
          if (!datasetId && token?.parentTokenId === null) {
            setStandaloneFormSubmitCompleted(true);
            return;
          }
          closeDatasetForm(
            newItemUrl ?? bulkDetailRedirect,
            additionalRouterState
          );
        }
      }
    },
    [
      formType,
      form?.apiMethods,
      dataset?.idItem,
      formData,
      formItems,
      selectedRows,
      currentSelectedRowIndex,
      actualToken,
      sessionToken,
      isSubmitting,
      insert,
      update,
      closeDatasetForm,
      multiSelectMode,
      handleApiError,
    ]
  );

  const fetchRowData = useCallback(
    async (
      currentRowIndex: number,
      resolvedDataset: Dataset,
      responseForm?: Form,
      // Optional cancel callback forwarded to openForceErrorModal (e.g. skip row on cancel)
      forceErrorCancelAction?: () => void
    ) => {
      const resolvedForm = responseForm || form;

      if (formType === 'insert' && resolvedForm) {
        return await fetchInsertRowData(
          resolvedForm,
          resolvedDataset,
          actualToken,
          sessionToken,
          fetchData,
          detailedFetchData,
          forceErrorCancelAction,
          getResponseActionHandler
        );
      }

      const resolveDatasetDataKey = getDataKey(resolvedDataset);
      // token meta.rowsToUse now stores ids only (string[]) — see createRootTokenByFormType
      const tokenRowIds = actualToken?.meta?.rowsToUse as string[] | undefined;
      const hasSelectedRows =
        selectedRows.length && selectedRows[currentRowIndex];
      const hasRowsFromTokenMeta = tokenRowIds && tokenRowIds[currentRowIndex];

      // Cold-start via external URL: token chain + selectedRows are in-memory
      // only and empty after reload. Recover the source row id from URL params
      // so edit/copy can pre-populate. Index 0 only — no list to paginate.
      let urlRowId: string | undefined = undefined;
      if (currentRowIndex === 0) {
        if (formType === 'copy') {
          urlRowId = sourceItemId ?? subsetItemId;
        }
        if (formType === 'edit') {
          urlRowId = subsetItemId ?? itemId;
        }
      }
      const hasUrlRowId = Boolean(urlRowId);

      // copy uses the same get-item-values fetch as edit to pre-populate form fields
      if (
        (formType === 'edit' || formType === 'copy') &&
        resolvedForm &&
        resolvedDataset.idItem &&
        (hasSelectedRows || hasRowsFromTokenMeta || hasUrlRowId)
      ) {
        if (hasRowsFromTokenMeta && tokenRowIds) {
          // Rebuild minimal row stubs so downstream readers doing row[dataKey] still work
          setSelectedRows(
            tokenRowIds.map((id) => ({ [resolveDatasetDataKey]: id }))
          );
        } else if (!hasSelectedRows && hasUrlRowId && urlRowId) {
          // Seed selectedRows from URL so submit/close path has a row to reference
          setSelectedRows([{ [resolveDatasetDataKey]: urlRowId }]);
        }

        let rowId: string | undefined;
        if (hasRowsFromTokenMeta) {
          rowId = tokenRowIds?.[currentRowIndex];
        } else if (hasSelectedRows) {
          rowId = selectedRows[currentRowIndex][resolveDatasetDataKey];
        } else {
          rowId = urlRowId;
        }

        if (!rowId) return;

        return await fetchEditRowData(
          resolvedForm,
          resolvedDataset,
          rowId,
          formType,
          actualToken,
          sessionToken,
          fetchData,
          detailedFetchData,
          forceErrorCancelAction,
          getResponseActionHandler
        );
      }
    },
    [
      dataset,
      formType,
      selectedRows,
      form,
      actualToken,
      state,
      sourceItemId,
      itemId,
      subsetItemId,
    ]
  );

  const showNextRow = useCallback(
    // skipRow=true skips the immediately previous row (go +2); used as cancelAction on force error
    async (skipRow = false) => {
      // Ignore repeat arrow clicks while a row fetch is already running
      if (isFetchingIncomingRowRef.current && !skipRow) return;
      const newIndex = currentSelectedRowIndex + (skipRow ? 2 : 1);
      if (newIndex < selectedRows.length && dataset && form) {
        isFetchingIncomingRowRef.current = true;
        setIsFetchingIncomingRow(true);
        try {
          const fetchedRowData = await fetchRowData(
            newIndex,
            dataset,
            undefined,
            () => showNextRow(true)
          );
          if (fetchedRowData) {
            setCurrentSelectedRowIndex(newIndex);
            setFormInputData(fetchedRowData, form, false);
          }
        } finally {
          // Always release the lock — covers failure and empty-result paths too
          isFetchingIncomingRowRef.current = false;
          setIsFetchingIncomingRow(false);
        }
      }
    },
    [currentSelectedRowIndex, selectedRows, fetchRowData, form, dataset]
  );

  const showPreviousRow = useCallback(
    // skipRow=true skips the immediately previous row (go -2); used as cancelAction on force error
    async (skipRow = false) => {
      // Ignore repeat arrow clicks while a row fetch is already running
      if (isFetchingIncomingRowRef.current && !skipRow) return;
      const newIndex = currentSelectedRowIndex - (skipRow ? 2 : 1);
      if (newIndex >= 0 && dataset && form) {
        isFetchingIncomingRowRef.current = true;
        setIsFetchingIncomingRow(true);
        try {
          const fetchedRowData = await fetchRowData(
            newIndex,
            dataset,
            undefined,
            () => showPreviousRow(true)
          );
          if (fetchedRowData) {
            setCurrentSelectedRowIndex(newIndex);
            setFormInputData(fetchedRowData, form, false);
          }
        } finally {
          // Always release the lock — covers failure and empty-result paths too
          isFetchingIncomingRowRef.current = false;
          setIsFetchingIncomingRow(false);
        }
      }
    },
    [currentSelectedRowIndex, fetchRowData, form, dataset]
  );

  // Submits the current edit row and navigates forward without closing the form
  const submitAndNext = useCallback(
    () => submitDatasetForm(showNextRow),
    [submitDatasetForm, showNextRow]
  );

  const handleInputChange = useCallback(
    (name: string, value: GridInputValue) => {
      setFormData((oldFormData) => {
        const newFormData = {
          ...oldFormData,
          [name]: value,
        };
        return newFormData;
      });
    },
    []
  );

  const patchFormData = useCallback((patch: Record<string, unknown>) => {
    setFormData((oldFormData) => ({
      ...oldFormData,
      ...patch,
    }));
  }, []);

  /* Persist formData + current row index together. Must stay in one effect —
     splitting lets the later write fire with a stale token closure and clobber
     the earlier meta update. Row index in deps so navigation reschedules the timer. */
  useDebouncedEffect(
    () => {
      const tokenFromUrl = getActualTokenFromUrl();

      if (tokenFromUrl && dataFromTokenInitialised.current) {
        updateTokenValues({
          ...tokenFromUrl,
          values: formData,
          meta: {
            ...tokenFromUrl.meta,
            currentSelectedRowIndex,
          },
        });
      }
    },
    [formData, currentSelectedRowIndex],
    300
  );

  /* Fetch form data  and missing datasets (when opening in the middle of the chain or when from external url)*/
  useDebouncedEffect(
    () => {
      const fetchFormData = async (dataset: Dataset) => {
        // Suppress the generic ApiError screen while the initial row fetch is
        // in flight — any ForceError (HTTP 449) must be handled by
        // ForceErrorModal alone. Mirrors the guard in showNextRow / showPreviousRow.
        isFetchingIncomingRowRef.current = true;
        setIsFetchingIncomingRow(true);
        try {
          const fetchedForm = await fetchForm();
          const rowData = await fetchRowData(
            currentSelectedRowIndex,
            dataset,
            fetchedForm,
            // Cancel from ForceErrorModal on initial load → back to origin (DatasetList or Detail via token.fromUrl)
            closeDatasetForm
          );

          if (fetchedForm) {
            const relevantFormData = createRelevantFormData(
              rowData,
              fetchedForm
            );
            const map = createFormItemsMap(
              fetchedForm.items,
              fetchedForm.groups,
              fetchedForm.sections
            );
            const normalizedMap = normalizeFormItemMap(map);
            await initializeFormItems(
              normalizedMap,
              relevantFormData as JSONObject
            );
            // Token-merge effect will flip isLoading once it merges values; fallback flip here when it cannot run.
            if (!rowData) {
              setIsLoading(false);
            }
          }

          setFormInputData(rowData, fetchedForm, false);
        } finally {
          // Always release the lock — covers failure and force-cancel paths
          isFetchingIncomingRowRef.current = false;
          setIsFetchingIncomingRow(false);
        }
      };

      const fetchMissingSlaveDataset = async (subsetId: string) => {
        const newDatasets = await fetchDataset();
        processFetchedSlaveDataset(
          newDatasets,
          subsetId,
          setSlaveDatasets,
          setSlaveLazyStates
        );
      };

      if (displaySlaveDataset && subsetId && !dataset) {
        fetchMissingSlaveDataset(subsetId);
      }

      // Wait for the root token before fetching: on a fresh-loaded form URL the
      // token is created in a mount effect (then history.replace adds ?token=).
      // Its meta.requestData carries master.id used by {{ master.id }} in the api
      // path — fetching before it exists would resolve an empty master segment.
      if (
        actualToken &&
        !rowData &&
        !form &&
        dataset &&
        !formLoading &&
        !dataLoading
      ) {
        fetchFormData(dataset);
      }
    },
    [dataset, actualToken],
    500
  );

  /* Assign values from token to form data - need to be debounced to avoid race conditions */
  useDebouncedEffect(
    () => {
      if (actualToken && rowData && form && !dataFromTokenInitialised.current) {
        setFormInputData(rowData, form, true);
        dataFromTokenInitialised.current = true;
        setIsLoading(false);
      }
    },
    [form, rowData, formTokens, search, state, pathname],
    500
  );

  /* Release loading on any API error so DatasetForm can render the ApiError screen. */
  useEffect(() => {
    if (apiError) {
      setIsLoading(false);
    }
  }, [apiError]);

  /* Remove descendant tokens when opening in the middle of the chain */
  useDebouncedEffect(
    () => {
      const tokenFromUrl = getActualTokenFromUrl();

      if (tokenFromUrl && !descendantRemoveChecked.current) {
        removeDescendantTokens(tokenFromUrl.id);
        descendantRemoveChecked.current = true;
        return;
      }
    },
    [getActualTokenFromUrl, formTokens],
    500
  );

  /* Create new root token when getting non existing token from URL */
  useEffect(() => {
    const tokenFromUrl = getActualTokenFromUrl();
    // Keep navigation state valid when only one token branch is present after history.replace.
    const urlHasInvalidToken =
      (!tokenFromUrl &&
        !state?.rootToken &&
        !state?.childToken &&
        search.includes('token')) ||
      !search.includes('token');

    if (urlHasInvalidToken) {
      // Subset forms resolve api method paths with {{ master.id }} (e.g.
      // engine/4.1/master/{{ master.id }}/slave/0). The control-action flow seeds
      // this via requestData; a fresh start (direct URL, no token) must derive the
      // master id from the URL itemId or the path renders with an empty segment.
      const requestData =
        isSubsetForm && itemId
          ? {
              master: {
                id: itemId,
                idItem: getDataKey(masterDataset),
              },
            }
          : undefined;

      const newRootToken = createRootToken({
        url: pathname,
        fromUrl: resolveFromUrl(datasetId, state?.menuFromUrl, routes),
        meta: {
          // Form label fallback
          controlLabel: datasetId
            ? `${dataset?.label} ${getActionFromFormRouteUrl(pathname)}`
            : getActionFromFormRouteUrl(pathname),
          ...(requestData ? { requestData } : {}),
        },
      });

      history.replace(newRootToken.url, { rootToken: newRootToken });
    }

    return () => {
      // Full selection reset on unmount (selectedRows + selectAllChecked + virtual count)
      // so the "select all" header checkbox does not linger checked after leaving the form.
      resetSelectedRows();
    };
  }, []);

  /* Handle breadcrumb assignment into token */
  useEffect(() => {
    const actualToken = getActualTokenFromUrl();

    if (canAssignBreadCrumb && actualToken) {
      // Set data for breadcrumbs
      const breadCrumbLabel = getFormLabel(formType, form, dataset, pathname);
      const retypedToken = actualToken as FormTokenNode;

      updateTokenValues({
        ...retypedToken,
        meta: {
          ...retypedToken.meta,
          breadCrumbLabel,
        },
      });
    }
  }, [canAssignBreadCrumb]);

  const context = useMemo(
    () => ({
      isProvided: true,
      actualToken,
      apiError,
      currentSelectedRowIndex,
      multiSelectMode,
      controlSelectionMode,
      formValuesChanged,
      forceSave,
      setForceSave,
      dataset,
      isLoading,
      isSubmitting,
      form,
      formType,
      formData,
      formItems,
      formErrors,
      initialValues: rowData?.data?.[0],
      setFormErrors,
      submitDatasetForm,
      submitAndNext,
      closeDatasetForm,
      showNextRow,
      showPreviousRow,
      handleInputChange,
      patchFormData,
      prefetchedFormOptions,
      setPrefetchedFormOptions,
      standaloneFormSubmitCompleted,
    }),
    [
      actualToken,
      apiError,
      currentSelectedRowIndex,
      multiSelectMode,
      controlSelectionMode,
      formValuesChanged,
      forceSave,
      dataset,
      isLoading,
      isSubmitting,
      form,
      formType,
      formData,
      formItems,
      formErrors,
      rowData,
      setForceSave,
      setFormErrors,
      submitDatasetForm,
      submitAndNext,
      closeDatasetForm,
      showNextRow,
      showPreviousRow,
      handleInputChange,
      patchFormData,
      prefetchedFormOptions,
      setPrefetchedFormOptions,
      standaloneFormSubmitCompleted,
    ]
  );

  // Build requestData for JSONata resolving in form inputs and controls
  const formRequestData = useMemo<FormApiMethodsRequestData>(() => {
    const tokenRequestData = actualToken?.meta
      ?.requestData as FormApiMethodsRequestData;

    const resolveFormData = {
      ...(rowData?.data?.[0] ?? {}),
      ...(formData ?? {}),
    };

    // Only edit/copy operate on an existing row; insert has no edited row id.
    const editedRow =
      formType === 'edit' || formType === 'copy'
        ? rowData?.data?.[0]
        : undefined;
    const currentRowId = editedRow?.[getDataKey(dataset)];

    return getFormApiMethodsRequestData({
      tokenRequestData,
      formType,
      mode: multiSelectMode,
      sessionId: sessionToken ?? '',
      form: resolveFormData,
      currentRowId,
    });
  }, [
    actualToken,
    sessionToken,
    formData,
    rowData?.data?.[0],
    dataset,
    formType,
    multiSelectMode,
  ]);

  return (
    <DatasetFormDataContext.Provider value={context}>
      <RequestDataProvider requestData={formRequestData}>
        <FormHandlersProvider>{children}</FormHandlersProvider>
      </RequestDataProvider>
    </DatasetFormDataContext.Provider>
  );
};

export default DatasetFormDataContextProvider;
